import React from 'react';
import Layout from '../../components/Layout';
import '../../Styles/Product.scss';
import product1 from '../../assets/webp_images/product_1.webp';
import calibratePhone from '../../assets/icons/callibrate_phone.svg';
import circles_product from '../../assets/icons/circles_product.svg';
import circles2_product from '../../assets/icons/circles2_product.svg';
import line from '../../assets/icons/line.svg';
import { Link } from 'react-router-dom';
import { useUser } from '../../contexts/userContext';
import { usePlatformDetect } from '../../hooks/usePlatformDetect.hook';
import { Helmet } from 'react-helmet';
import quoteTop from '../../assets/icons/quote_top.svg';
import quoteBottom from '../../assets/icons/quote_bottom.svg';
import testimonials from '../../assets/images/testimonials_photo.png';
import { Carousel } from 'react-responsive-carousel';
import petapixel from '../../assets/icons/petapixel.svg';

const Product = () => {
  const {
    state: { subscribed, isLoggedIn }
  } = useUser();

  const {
    deviceInfo: { device, platform }
  } = usePlatformDetect();

  const redirectAppStore = `https://apps.apple.com/us/app/TRUHu/id${process.env.REACT_APP_APPLE_APP_ID}/`;

  return (
    <Layout>
      <div className="bgColor">
        <Helmet>
          <title>TruHu - Monitor Color Calibration App | How It Works</title>
          <meta
            name="description"
            content="​The TruHu monitor calibration app uses your phone’s camera to capture the performance of your screen and create an optimized ICC monitor profile."
          />
        </Helmet>
        <div className="first-section__product">
          <div className="first-section-text__product">
            <h1>Monitor Color Calibration App</h1>
            <h2 className="first-section-second-text">
              See color the way it
              <br /> was intended
            </h2>
            <p>
              Using both a desktop and mobile app together, <br /> TruHu brings color accuracy to
              your monitors!
            </p>
            <div className="links__product">
              <img src={circles_product} alt="" className="circles_product" />
              <img src={line} alt="" className="line__product" />
            </div>
            <div className="links__payment">
              {!subscribed && (
                <>
                  <Link to={isLoggedIn ? '/payment/buy' : 'signin'}>
                    <div className="actionLinkContainer">
                      <button className="subscribe__button">Subscribe</button>
                      <p className="label">($29.99/yr)</p>
                    </div>
                  </Link>
                </>
              )}
              {subscribed && (
                <>
                  {platform === 'Windows' || device === 'Android' ? (
                    <Link to={'/wait-list'}>
                      <button className="subscribe__button">Join the Wait List</button>
                    </Link>
                  ) : (
                    <a href={platform !== 'MacOs' ? redirectAppStore : '/downloads'}>
                      <button className="subscribe__button">Download</button>
                    </a>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="gradient__product">
            <img src={circles2_product} alt="" className="circles2__product" />
            <img src={product1} alt="" className="product1" />
          </div>
        </div>
        <div className="second-section__product">
          <img src={calibratePhone} alt="" className="calibratePhone" />
          <div className="second-section-text__product">
            <h2>Calibrate in a Few Easy Steps</h2>
            <br />
            <ol>
              <li>
                <div>
                  <h4>Adjust ambient lighting</h4>
                  <p>
                    Make sure no light falls on your screen and that the ambient light is similar to
                    how you typically work.
                  </p>
                </div>
              </li>
              <li>
                <div>
                  <h4>Set brightness</h4>
                  <p>
                    Set brightness to a level appropriate for the ambient light in your work
                    environment.
                  </p>
                </div>
              </li>
              <li>
                <div>
                  <h4> Set color temperature</h4>
                  <p>
                    TruHu lets you select between multiple color temperature settings, including
                    your current profile.
                  </p>
                </div>
              </li>
              <li>
                <div>
                  <h4>Capture grey</h4>
                  <p>
                    Point the camera at the grey target on the monitor until the yellow rectangle
                    and the green square line up. Then take the photo.
                  </p>
                </div>
              </li>
              <li>
                <div>
                  <h4>Capture color</h4>
                  <p>
                    Point the camera at the color target on the monitor until the yellow rectangle
                    and the green square line up. Then take the photo.
                  </p>
                </div>
              </li>
              <li className="lastItem">
                <div>
                  <h4>Calibration complete</h4>
                  <p>Your new color profile is ready to use. </p>
                </div>
              </li>
            </ol>
          </div>
        </div>
        <div className="third-section__product">
          <h2>
            Supported Devices and
            <br /> Operating Systems
          </h2>
          <ol>
            <li>
              <div>
                <p>macOS – Mojave / 10.14 and later </p>
              </div>
            </li>
            <li>
              <div>
                <p>iPhone 7 and later</p>
              </div>
            </li>
            <li>
              <div>
                <p>Windows / Android coming later in 2023</p>
              </div>
            </li>
          </ol>
          <a href={isLoggedIn ? '/payment/try' : 'signin'}>
            <div className="actionLinkContainer">
              <button className="free-trial__button">Try it</button>
              <p className="label">(Free)</p>
            </div>
          </a>
        </div>
        <div className="technical-explainer">
          <h2>How TruHu Works Its Magic</h2>
          <p className="technical-explainer-paragraph">
            Our monitor color calibration tool uses two apps in tandem - one desktop and one mobile
            - to analyze color data from your screen, <br /> determine its backlight technology, and
            create an optimized color profile, all done in one minute.
          </p>
          <div className="details">
            <div>
              <h3>Colorimetric Accuracy</h3>
              <p>
                Using image sensor data from your phone’s camera, TruHu bypasses your device’s
                standard image processing to send RAW sensor measurements through our proprietary
                algorithms, converting them to colorimetric data. The results are very similar in
                quality to those generated by colorimeter-based monitor calibration products
                (CBMCPs).
              </p>
            </div>
            <div>
              <h3>Luminance</h3>
              <p>
                The default luminance on most CBMCPs is 120 cd/m2 (or NITS). This is not bright
                enough for many working environments, which can have significant overhead or natural
                light. TruHu trusts you to know the appropriate brightness level for your working
                environment, rather than forcing you into an arbitrary luminance number.
              </p>
            </div>
            <div>
              <h3>Metamerism</h3>
              <p>
                Colorimeters ”see” colors differently based on the backlight technology of your
                monitor. Your phone’s camera has the same limitation. With TruHu, you don’t have to
                know all those technical details - we use an extensive database of displays and
                their respective backlight technologies to automatically determine the best way to
                calibrate your screen and thus minimize metamerism.
              </p>
            </div>
          </div>
        </div>
        <h2 className="testimonials-header">What TruHu Users Say…</h2>
        <div className="forth-section__product">
          <img src={testimonials} alt="" className="testimonialsImg" />
          <div className="testimonials">
            <div className="testimonialsContent">
              <img src={quoteTop} alt="" className="quote-top" />
              <Carousel showThumbs={false} infiniteLoop interval={5000} autoPlay={true}>
                <div style={{ textAlign: 'center', margin: 'auto' }}>
                  <div>
                    <p>
                      I got similar results [to colorimeters] in a fraction of the time, at a
                      fraction of the cost, and using just my phone. TruHu is the perfect gateway to
                      a better and more consistent color. For many users, it’s the last calibration
                      technology they’ll need
                    </p>
                    <div className="author">
                      <img src={petapixel} alt="petapixel" />
                      <p>
                        <a
                          href="https://petapixel.com/2023/06/06/truhu-review-a-simple-way-to-calibrate-monitors-with-a-smartphone/"
                          target="blank">
                          PetaPixel - TruHu Review
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
                <div style={{ margin: 'auto', textAlign: 'center' }}>
                  <div>
                    <p>
                      ‘I used to move designs from one screen to another hoping the colors still
                      look the same -{' '}
                      <span style={{ color: '#35c9eb', fontStyle: 'italic' }}>
                        after using TruHu,
                      </span>{' '}
                      I know they will. And I also know my team&apos;s designs will show up the way
                      they were intended. It was particularly satisfying when I got the new color
                      profiles and saw the difference in real-time across my entire monitor setup.
                      <br />
                      <br />
                      Honestly, TruHu is so great it begs the question - why hasn&apos;t anyone
                      thought of this earlier?
                      <br />
                      Truly worth every penny! ’
                    </p>
                    <div className="author">
                      <p className="author-name">Bogdan Minuț</p>
                      <p>Content Marketing Strategist, Digital Tailors Agency</p>
                    </div>
                  </div>
                </div>
              </Carousel>
              <img src={quoteBottom} alt="" className="quote-bottom" />
            </div>
          </div>
        </div>
        {/* <Newsletter /> */}
      </div>
    </Layout>
  );
};

export default Product;
